import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import cn from 'classnames';
import { buildSeoTitle, buildMetaDescription } from 'shared/lib/seoHelpers';
import Page from 'userportal/components/Page';
import Subheader from 'shared/components/Subheader';
import { userportal as routes, createUserportalLink } from 'shared/routes';
import { ALL_JOBS_LINK } from 'shared/constants';
import { selectors } from 'userportal/ducks/auth';
import styles from './HowItWorks.module.scss';

const Ring = ({ value, large }) => (
  <div
    className={cn(styles.ring, { [styles.ringLarge]: large })}
    style={{ background: `conic-gradient(#f5a623 0 ${value}%, #e9ecf5 ${value}%)` }}
  >
    <div>{value} %</div>
  </div>
);

const HowItWorks = () => {
  const { t } = useTranslation('userportal');
  const isLoggedIn = useSelector(selectors.isLoggedIn);
  const scoreName = t('common:scoreName');

  const dims = [
    { label: t('common:score.dim.capabilities'), value: 71, tone: styles.toneOrange },
    { label: t('common:score.dim.overview'), value: 84, tone: styles.toneGreen },
    { label: t('common:score.dim.trajectory'), value: 66, tone: styles.toneOrange },
    { label: t('common:score.dim.context'), value: 48, tone: styles.toneOrange },
    { label: t('common:score.dim.domain'), value: 70, tone: styles.toneOrange },
  ];

  return (
    <Page
      initialContainer={false}
      subheader={(
        <Subheader center elementType="h1" title={t('page.hiw.title')}>
          <span>{t('page.hiw.subtitle')}</span>
        </Subheader>
      )}
      helmetTitle={buildSeoTitle(t, t('page.hiw.title'))}
      helmetDescription={buildMetaDescription(`${t('page.hiw.step1.p1')} ${t('page.hiw.step2.p2')}`)}
      seoUrl={createUserportalLink(routes.howItWorks)}
    >
      <div className={styles.page}>

        <section className={styles.timeline}>

          <div className={styles.stepRow}>
            <div className={styles.stepRail}>
              <div className={styles.stepNum}>1</div>
              <div className={styles.stepLine} />
            </div>
            <div className={styles.stepBody}>
              <div className={styles.cols}>
                <div className={styles.colText}>
                  <h2>{t('page.hiw.step1.title')}</h2>
                  <p>{t('page.hiw.step1.p1')}</p>
                  <p>{t('page.hiw.step1.p2')}</p>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t('navigation.myProfile')}</div>
                  <div className={styles.upload}>
                    <div className={styles.uploadIcon}>PDF</div>
                    <div className={styles.uploadInfo}>
                      <div className={styles.uploadName}>Jan_Novak_CV.pdf</div>
                      <div className={styles.uploadSize}>240 kB</div>
                    </div>
                    <div className={styles.uploadDone}>
                      <span>✓</span>
                      {t('page.hiw.visual1.processed')}
                    </div>
                  </div>
                  <div className={styles.profileCreated}>{t('page.hiw.visual1.created')}</div>
                  <div className={styles.profileGroups}>
                    <div>
                      <div className={styles.groupLabel}>{t('page.hiw.visual1.roles')}</div>
                      <div className={styles.tags}>
                        <span className={cn(styles.tag, styles.tagRole)}><i />Frontend developer</span>
                        <span className={cn(styles.tag, styles.tagLight)}>{t('page.hiw.visual1.roleYears')}</span>
                      </div>
                    </div>
                    <div>
                      <div className={styles.groupLabel}>{t('page.hiw.visual1.skills')}</div>
                      <div className={styles.tags}>
                        <span className={cn(styles.tag, styles.tagPink)}>React *</span>
                        <span className={cn(styles.tag, styles.tagPink)}>Typescript *</span>
                        <span className={cn(styles.tag, styles.tagGreen)}>Node.js</span>
                        <span className={cn(styles.tag, styles.tagGrey)}>{t('page.hiw.visual1.moreSkills')}</span>
                      </div>
                    </div>
                    <div>
                      <div className={styles.groupLabel}>{t('page.hiw.visual1.languages')}</div>
                      <div className={styles.tags}>
                        <span className={cn(styles.tag, styles.tagBrown)}>{t('page.hiw.visual1.langTag')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.stepRow}>
            <div className={styles.stepRail}>
              <div className={styles.stepNum}>2</div>
              <div className={styles.stepLine} />
            </div>
            <div className={styles.stepBody}>
              <h2>{t('page.hiw.step2.title')}</h2>
              <div className={styles.cols}>
                <p>
                  <Trans i18nKey="userportal:page.hiw.step2.p1" values={{ scoreName }}>
                    <strong />
                  </Trans>
                </p>
                <p>{t('page.hiw.step2.p2')}</p>
              </div>
              <div className={cn(styles.card, styles.cardFlush)}>
                <div className={styles.sortBar}>
                  <span>{t('common:jobs.sortByLabel')}</span>
                  <span className={styles.sortSelect}>{t('common:jobs.sortByScoreDesc')} <i>▲▼</i></span>
                </div>
                <div className={styles.jobList}>
                  <div className={styles.jobRow}>
                    <Ring value={69} />
                    <div className={styles.jobInfo}>
                      <div className={styles.jobTitle}>FRONTEND DEVELOPER - React + TypeScript</div>
                      <div className={styles.jobCompany}>Vertrino</div>
                      <div className={styles.jobLocation}>{t('page.hiw.visual2.loc1')}</div>
                      <p className={styles.jobDesc}>{t('page.hiw.visual2.job1Desc')}</p>
                      <div className={styles.tags}>
                        <span className={cn(styles.tag, styles.tagRole)}>{t('page.hiw.visual2.salary1')}</span>
                        <span className={cn(styles.tag, styles.tagLight)}>{t('page.hiw.visual2.contract1')}</span>
                        <span className={cn(styles.tag, styles.tagPink)}>React *</span>
                        <span className={cn(styles.tag, styles.tagOrange)}>Vite *</span>
                      </div>
                    </div>
                  </div>
                  <div className={cn(styles.jobRow, styles.jobRowDimmed)}>
                    <Ring value={60} />
                    <div className={styles.jobInfo}>
                      <div className={styles.jobTitle}>Generative AI Developer</div>
                      <div className={styles.jobCompany}>Kaledo Talent s.r.o.</div>
                      <div className={styles.jobLocation}>{t('page.hiw.visual2.loc2')}</div>
                      <div className={styles.tags}>
                        <span className={cn(styles.tag, styles.tagRole)}>{t('page.hiw.visual2.salary2')}</span>
                        <span className={cn(styles.tag, styles.tagLight)}>{t('page.hiw.visual2.contract2')}</span>
                        <span className={cn(styles.tag, styles.tagGreen)}>Python *</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.scorePanel}>
                  <div className={styles.scorePanelTitle}>{scoreName}</div>
                  <div className={styles.scorePanelSub}>{t('common:score.your', { scoreName, score: 69 })}</div>
                  <div className={styles.dims}>
                    {dims.map(dim => (
                      <div key={dim.label}>
                        <div className={styles.dimHead}>
                          <span>{dim.label}</span>
                          <span className={dim.tone}>{dim.value} %</span>
                        </div>
                        <div className={styles.dimTrack}>
                          <div className={dim.tone} style={{ width: `${dim.value}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.stepRow}>
            <div className={styles.stepRail}>
              <div className={styles.stepNum}>3</div>
              <div className={styles.stepLine} />
            </div>
            <div className={styles.stepBody}>
              <div className={styles.kicker}>{t('page.hiw.step3.kicker')}</div>
              <h2>{t('page.hiw.step3.title')}</h2>
              <div className={styles.cols}>
                <p>{t('page.hiw.step3.p1')}</p>
                <p>{t('page.hiw.step3.p2')}</p>
              </div>
              <div className={cn(styles.card, styles.cardFlush, styles.reviewCard)}>
                <div className={styles.reviewHead}>
                  <div className={styles.reviewTitle}>
                    {t('page.job.userJobReview.modalTitle')}
                    <span>{t('page.job.userJobReview.modalSubtitle')}</span>
                  </div>
                  <div className={styles.verdictBadge}><i />{t('page.job.userJobReview.verdict.STRETCH')}</div>
                  <div className={styles.reviewSummary}>
                    <div className={styles.reviewScore}>
                      <Ring value={69} large />
                      <div className={styles.reviewScoreLabel}>{t('page.job.userJobReview.scoreLabel')}</div>
                      <div className={styles.reviewScoreHint}>{t('page.job.userJobReview.scoreHint')}</div>
                    </div>
                    <div>
                      <div className={styles.reviewHeadline}>{t('page.hiw.visual3.headline')}</div>
                      <p className={styles.reviewView}>{t('page.hiw.visual3.view')}</p>
                    </div>
                  </div>
                </div>
                <div className={styles.reviewSection}>
                  <div className={styles.reviewSectionTitle}>{t('page.job.userJobReview.leadsTitle')} <span>4</span></div>
                  <div className={styles.reviewSectionSub}>{t('page.job.userJobReview.leadsSub')}</div>
                  <div className={styles.lead}>
                    <span className={styles.leadStar}>★</span>
                    <div>
                      <div className={styles.itemTitle}>{t('page.hiw.visual3.strength1')}</div>
                      <div className={cn(styles.quote, styles.quoteGreen)}>
                        <div className={styles.quoteLabel}>{t('page.job.userJobReview.howToSell')}</div>
                        <p>{t('page.hiw.visual3.sell1')}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.reviewSection}>
                  <div className={styles.reviewSectionTitle}>{t('page.job.userJobReview.concernsTitle')} <span>4</span></div>
                  <div className={styles.reviewSectionSub}>{t('page.job.userJobReview.concernsSubStretch')}</div>
                  <div className={styles.concerns}>
                    <div>
                      <div className={styles.concernHead}>
                        <span className={styles.gapBadge}>{t('page.job.userJobReview.tagReal')}</span>
                        <div className={styles.itemTitle}>{t('page.hiw.visual3.concern1')}</div>
                      </div>
                      <div className={styles.concernAction}>↳ {t('page.hiw.visual3.concern1Action')}</div>
                    </div>
                    <div>
                      <div className={styles.concernHead}>
                        <span className={styles.gapBadge}>{t('page.job.userJobReview.tagReal')}</span>
                        <div className={styles.itemTitle}>{t('page.hiw.visual3.concern2')}</div>
                      </div>
                      <div className={styles.concernAction}>↳ {t('page.hiw.visual3.concern2Action')}</div>
                    </div>
                  </div>
                </div>
                <div className={styles.reviewSection}>
                  <div className={styles.reviewSectionTitle}>{t('page.job.userJobReview.devTitleStretch')} <span>4</span></div>
                  <div className={styles.reviewSectionSub}>{t('page.job.userJobReview.devSubStretch')}</div>
                  <div className={styles.lead}>
                    <span className={styles.devNum}>1</span>
                    <div>
                      <div className={styles.devHead}>
                        <span className={styles.itemTitle}>{t('page.hiw.visual3.dev1')}</span>
                        <span className={styles.devTime}>{t('page.hiw.visual3.dev1Time')}</span>
                      </div>
                      <div className={styles.devWhy}>{t('page.hiw.visual3.dev1Why')}</div>
                      <div className={cn(styles.quote, styles.quoteOrange)}>
                        <div className={styles.quoteLabel}>{t('page.job.userJobReview.howTo')}</div>
                        <p>{t('page.hiw.visual3.dev1How')}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.reviewSection}>
                  <div className={styles.reviewSectionTitle}>{t('page.job.userJobReview.prepTitle')} <span>5</span></div>
                  <div className={styles.reviewSectionSub}>{t('page.job.userJobReview.prepSub')}</div>
                  <div className={styles.questions}>
                    <div className={styles.question}>
                      <div className={styles.questionHead}>
                        <span className={styles.questionNum}>1</span>
                        <span className={styles.questionText}>{t('page.hiw.visual3.q1')}</span>
                        <i>▲</i>
                      </div>
                      <div className={styles.questionBody}>
                        <div className={styles.quoteLabel}>{t('page.job.userJobReview.goodAnswer')}</div>
                        <div className={styles.answerList}>
                          <div><span>✓</span>{t('page.hiw.visual3.q1a1')}</div>
                          <div><span>✓</span>{t('page.hiw.visual3.q1a2')}</div>
                          <div><span>✓</span>{t('page.hiw.visual3.q1a3')}</div>
                        </div>
                      </div>
                    </div>
                    <div className={cn(styles.question, styles.questionHead)}>
                      <span className={styles.questionNum}>2</span>
                      <span className={styles.questionText}>{t('page.hiw.visual3.q2')}</span>
                      <i>▼</i>
                    </div>
                  </div>
                  <div className={styles.tipBox}>
                    <div className={styles.tipTitle}>{t('page.job.userJobReview.tipTitle')}</div>
                    <div className={styles.tipSub}>{t('page.job.userJobReview.tipSub')}</div>
                    <p>{t('page.hiw.visual3.tipText')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.stepRow}>
            <div className={styles.stepRail}>
              <div className={styles.stepNum}>4</div>
              <div className={styles.stepLine} />
            </div>
            <div className={styles.stepBody}>
              <h2>{t('page.hiw.step4.title')}</h2>
              <div className={styles.cols}>
                <p>{t('page.hiw.step4.p1')}</p>
                <p>{t('page.hiw.step4.p2')}</p>
              </div>
              <div className={cn(styles.card, styles.cardFlush)}>
                <div className={styles.applyHead}>
                  <div>
                    <div className={styles.jobTitle}>FRONTEND DEVELOPER - React + TypeScript</div>
                    <div className={styles.jobCompany}>Vertrino</div>
                  </div>
                  <span className={styles.applyButton}><i>✓</i>{t('page.job.applyButton')}</span>
                </div>
                <div className={styles.reactions}>
                  <div className={styles.reactionsTitle}>{t('page.user.applications.title')}</div>
                  <div className={styles.reactionRow}>
                    <div className={styles.reactionInfo}>
                      <div className={styles.reactionJob}>{t('page.hiw.visual4.r1Title')}</div>
                      <div className={styles.jobCompany}>Nordveta Systems s.r.o.</div>
                    </div>
                    <div className={styles.reactionStatus}>
                      <span className={styles.statusInterview}>{t('common:jaStatus.inteview')}</span>
                      <div>14.02.2026 18:29</div>
                    </div>
                  </div>
                  <div className={styles.reactionRow}>
                    <div className={styles.reactionInfo}>
                      <div className={styles.reactionJob}>{t('page.hiw.visual4.r2Title')}</div>
                      <div className={styles.jobCompany}>Nordveta Systems s.r.o.</div>
                    </div>
                    <div className={styles.reactionStatus}>
                      <span className={styles.statusDelivered}>{t('common:jaStatus.read')}</span>
                      <div>28.01.2026 23:44</div>
                    </div>
                  </div>
                  <div className={cn(styles.reactionRow, styles.reactionRowDimmed)}>
                    <div className={styles.reactionInfo}>
                      <div className={styles.reactionJob}>
                        {t('page.hiw.visual4.r3Title')} <span>({t('page.job.inactive')})</span>
                      </div>
                      <div className={styles.jobCompany}>Nordveta Systems s.r.o.</div>
                    </div>
                    <div className={styles.reactionStatus}>
                      <span className={styles.statusRejected}>{t('common:jaStatus.company-reject')}</span>
                      <div>04.09.2025 23:15</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.stepRow}>
            <div className={styles.stepRail}>
              <div className={styles.stepNum}>5</div>
            </div>
            <div className={cn(styles.stepBody, styles.stepBodyLast)}>
              <h2>{t('page.hiw.step5.title')}</h2>
              <div className={styles.cols}>
                <p>{t('page.hiw.step5.p1')}</p>
                <p>{t('page.hiw.step5.p2')}</p>
              </div>
              <div className={styles.offerCards}>
                <div className={styles.card}>
                  <div className={styles.reactionsTitle}>{t('page.user.offers.title')}</div>
                  <div className={styles.reactionRow}>
                    <div className={styles.reactionInfo}>
                      <div className={styles.reactionJob}>
                        PHP Developer (Remote) <span>({t('page.user.offers.inactivePosition')})</span>
                      </div>
                      <div className={styles.jobCompany}>Brightloop Engineering s.r.o.</div>
                    </div>
                    <div className={styles.reactionStatus}>
                      <span className={styles.statusDelivered}>{t('joStatus.sent')}</span>
                      <div>21.07.2025 15:19</div>
                    </div>
                  </div>
                  <div className={styles.reactionRow}>
                    <div className={styles.reactionInfo}>
                      <div className={styles.reactionJob}>Java/React developer</div>
                      <div className={styles.jobCompany}>Modrý Kompas s.r.o.</div>
                    </div>
                    <div className={styles.reactionStatus}>
                      <span className={styles.statusRead}>{t('joStatus.read')}</span>
                      <div>13.03.2025 18:36</div>
                    </div>
                  </div>
                  <div className={styles.offerChips}>
                    <span><i />{t('page.hiw.visual5.chipAnon')}</span>
                    <span><i />{t('page.hiw.visual5.chipNoSpam')}</span>
                  </div>
                </div>
                <div className={styles.card}>
                  <div className={styles.offerDetailHead}>
                    <div className={styles.offerLogo}>OV</div>
                    <div>
                      <div className={styles.reactionJob}>{t('page.hiw.visual5.dTitle')}</div>
                      <div className={styles.offerDetailMeta}>
                        <span className={styles.statusRead}>{t('joStatus.read')}</span>
                        <span className={styles.offerDetailLink}>{t('page.hiw.visual5.detailLink')}</span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.offerMessage}>
                    <p>{t('page.hiw.visual5.msg1')}</p>
                    <p>{t('page.hiw.visual5.msg2')}</p>
                    <p>{t('page.hiw.visual5.msg3')}</p>
                  </div>
                  <div className={styles.offerSent}>{t('page.user.offers.sent')}: 11.08.2025 14:46</div>
                  <div className={styles.offerActions}>
                    <span className={styles.offerAccept}><i>✓</i>{t('page.user.offers.acceptOffer')}</span>
                    <span className={styles.offerMessageBtn}>{t('page.user.offers.addMessage')}</span>
                    <span className={styles.offerReject}>{t('page.user.offers.rejectOffer')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </section>

        {!isLoggedIn && (
          <section className={styles.cta}>
            <h2>{t('page.hiw.cta.title')}</h2>
            <div className={styles.ctaButtons}>
              <Link to={routes.signup} className={styles.ctaPrimary}>{t('page.hiw.cta.signup')}</Link>
              <Link to={ALL_JOBS_LINK} className={styles.textLink}>{t('page.hiw.cta.browse')}</Link>
            </div>
          </section>
        )}
      </div>
    </Page>
  );
};

export default HowItWorks;
